/**
 * Servicio para el endpoint rutas/rangos
 *
 * Sustituye a src/services/rangos.service.js para todo código nuevo. El
 * servicio legacy se mantiene únicamente porque src/views/Stickers.js lo
 * sigue usando (ver TASK-016); este servicio no lo importa ni depende de él.
 *
 * Contrato inferido (NO confirmado con backend), documentado en detalle en
 * docs/api/rangos.md, incluyendo la lista de puntos sin verificar. Puntos
 * relevantes para leer este archivo:
 * - GET devuelve un array plano (sin envoltorio).
 * - POST devuelve { data, message }.
 * - PUT solo tiene confirmado { message }; que 'data' exista es una suposición.
 * - DELETE tiene forma de respuesta totalmente desconocida.
 */

import axios from "axios";
import HostService from "../../../services/host.service";
import AuthService from "../../../services/auth.service";

/**
 * Normaliza un rango de precios del backend (snake_case) a camelCase.
 * @param {Object} rango - Rango en formato backend (id_rango, id_agencia, ...)
 * @returns {Object} Rango en camelCase: { id, idAgencia, isla, rango, precio, palet }
 */
const normalizeRango = (rango) => ({
    id: rango.id_rango,
    idAgencia: rango.id_agencia,
    isla: rango.isla,
    rango: rango.rango,
    precio: rango.precio,
    palet: rango.palet,
});

/**
 * Construye el payload snake_case que espera el backend a partir de un
 * rango en camelCase (comparten los mismos 5 campos en create y update).
 * @param {Object} item - Rango en camelCase
 * @returns {Object} Payload en snake_case para el body de la petición
 */
const toPayload = (item) => ({
    id_agencia: item.idAgencia,
    rango: item.rango,
    precio: item.precio,
    palet: item.palet,
    isla: item.isla,
});

/**
 * Extrae un mensaje de error legible de una respuesta fallida de Axios.
 * DECISION: la forma del error body no está confirmada para ningún endpoint
 * de rangos (ver "Puntos sin verificar" en docs/api/rangos.md); se prueban
 * las formas ya usadas en otros *.api.js del proyecto antes de caer al mensaje
 * genérico de Axios.
 * @param {Object} error - Error capturado de Axios
 * @param {string} fallback - Mensaje genérico si no hay forma reconocida
 * @returns {string} Mensaje de error a mostrar
 */
const extractErrorMessage = (error, fallback) =>
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message ||
    fallback;

/**
 * Servicio para gestión de rangos de precios
 */
class RangosService {
    /**
     * GET rutas/rangos/ - Lista todos los rangos de precios
     *
     * @returns {Promise<Array>} Lista de rangos en camelCase, con 'id' listo
     *   para usar como identificador de fila del DataGrid sin mapeo manual
     */
    static async list() {
        return axios
            .get(HostService.getHost() + 'rutas/rangos/', AuthService.setToken())
            .then((res) => {
                // Cuerpo ausente o vacio -> lista vacia, como el resto de *.api.js del
                // proyecto (reponedores, peticiones, envios). El contrato no documenta
                // que forma tiene un GET sin resultados, y tratarlo como error mostraria
                // un toast donde deberia verse una tabla vacia.
                // Se comprueba con !res.data y no solo contra null/undefined porque un
                // 200 con body vacio llega como cadena vacia: el transformResponse de
                // axios solo hace JSON.parse si `data && typeof data === 'string'`
                if (!res.data) {
                    return [];
                }

                // Llegado aqui res.data tiene contenido pero no es un array, lo que si
                // evidencia un cambio de contrato (p. ej. envoltorio { data: [...] },
                // como el que devuelve POST). Se lanza con mensaje legible para que no
                // acabe en el toast el TypeError crudo de .map
                if (!Array.isArray(res.data)) {
                    throw new Error('La API devolvió un formato inesperado al listar rangos');
                }

                return res.data.map(normalizeRango);
            })
            .catch((error) => {
                console.error('Error al listar rangos de precios:', error);
                throw new Error(extractErrorMessage(error, 'Error al listar rangos de precios'));
            });
    }

    /**
     * POST rutas/rangos/crear/ - Crea un nuevo rango de precios
     *
     * @param {Object} item - Datos del rango a crear
     * @param {string} item.idAgencia - Código del transportista (CODIGO de DeliveryService.getTransports())
     * @param {string} item.rango - Rango(s) de código postal, texto libre separado por comas
     * @param {string|number} item.precio - Precio del bulto
     * @param {string|number} item.palet - Precio del palet
     * @param {string} item.isla - Isla (texto libre, sin catálogo cerrado confirmado)
     * @returns {Promise<Object>} Rango creado en camelCase, con el 'id' asignado por el backend
     */
    static async create(item) {
        return axios
            .post(HostService.getHost() + 'rutas/rangos/crear/', toPayload(item), AuthService.setToken())
            .then((res) => normalizeRango(res.data.data))
            .catch((error) => {
                console.error('Error al crear rango de precios:', error);
                throw new Error(extractErrorMessage(error, 'Error al crear rango de precios'));
            });
    }

    /**
     * PUT rutas/rangos/modificar/:id - Actualiza un rango de precios existente
     *
     * DECISION: solo está confirmado que la respuesta trae 'message' (ver
     * docs/api/rangos.md); que también traiga 'data' con el rango actualizado
     * es una suposición por simetría con POST, no un hecho verificado. Cuando
     * no llega 'data' se devuelve null, NO el item enviado: devolver la entrada
     * la haría indistinguible de una confirmación del servidor, y si el backend
     * normaliza o redondea algún campo el usuario vería lo que tecleó en vez de
     * lo persistido. Quien consuma debe recargar si necesita el estado real.
     *
     * @param {number} id - id_rango del rango a modificar (va en el path)
     * @param {Object} item - Mismos campos que create()
     * @returns {Promise<Object|null>} Rango actualizado en camelCase, o null si el
     *   backend no devolvió 'data' (la operación sí tuvo éxito: los fallos lanzan)
     */
    static async update(id, item) {
        return axios
            .put(HostService.getHost() + 'rutas/rangos/modificar/' + id, toPayload(item), AuthService.setToken())
            .then((res) => (res.data?.data ? normalizeRango(res.data.data) : null))
            .catch((error) => {
                console.error('Error al actualizar rango de precios:', error);
                throw new Error(extractErrorMessage(error, 'Error al actualizar rango de precios'));
            });
    }

    /**
     * DELETE rutas/rangos/eliminar?id={id} - Elimina un rango de precios
     *
     * DECISION: la forma de la respuesta es completamente desconocida (ver
     * "Puntos sin verificar" #1 en docs/api/rangos.md); el cliente legacy
     * nunca la lee. Este método no asume ninguna forma concreta: devuelve
     * res.data tal cual, sin transformar ni desenvolver.
     *
     * @param {number} id - id_rango del rango a eliminar (va como query param)
     * @returns {Promise<*>} Body de la respuesta sin transformar (forma no verificada)
     */
    static async delete(id) {
        return axios
            .delete(HostService.getHost() + 'rutas/rangos/eliminar?id=' + id, AuthService.setToken())
            .then((res) => res.data)
            .catch((error) => {
                console.error('Error al eliminar rango de precios:', error);
                throw new Error(extractErrorMessage(error, 'Error al eliminar rango de precios'));
            });
    }
}

export default RangosService;
